import React, { useState, useMemo } from "react";
import { BunkerTypesTable } from "./bunker-types-table";
import { BunkerTypeStatusFilter, BunkerTypeStatus } from "./bunker-type-status-filter";
import { BunkerTypeDialog } from "./bunker-type-dialog";
import {
  useListContainerTypesQuery,
  useUpdateContainerTypeMutation,
} from "../../../../shared/api/container-type/container-type.grpc";
import { useSelectedCompany } from "../../../../shared/store/company/hooks/useSelectedCompany";
import { ConnectProto, type ApiError } from "@racoon/api";
import { useT, Namespace, useApiErrorMessage } from "@racoon/i18n";
import { useToast } from "@racoon/ui";
import { useCanWriteType } from "~/shared/hooks/use-can-write-type";

type ContainerType = ConnectProto.ContainerType;

const isArchived = (type: ContainerType) =>
  type.status === ConnectProto.ContainerTypeStatus.INACTIVE;

interface BunkerTypesSettingsProps {
  selectedCompanyId?: number;
}

export const BunkerTypesSettings: React.FC<BunkerTypesSettingsProps> = ({
  selectedCompanyId: propSelectedCompanyId,
}) => {
  const t = useT();
  const { toast } = useToast();
  const describeError = useApiErrorMessage();
  const { selectedCompanyId: storeSelectedCompanyId } = useSelectedCompany();
  const selectedCompanyId = propSelectedCompanyId ?? storeSelectedCompanyId;

  const [selectedType, setSelectedType] = useState<ContainerType | null>(null);
  const [statusFilter, setStatusFilter] = useState<BunkerTypeStatus>("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ContainerType | null>(null);

  const { data: containerTypesResponse, isLoading } = useListContainerTypesQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );

  const [updateContainerType, { isLoading: isUpdating }] = useUpdateContainerTypeMutation();

  const containerTypes = containerTypesResponse?.containerTypes || [];

  const filteredTypes = useMemo(
    () => containerTypes.filter((type) => isArchived(type) === (statusFilter === "archived")),
    [containerTypes, statusFilter],
  );

  const statusCounts = useMemo(() => {
    const archived = containerTypes.filter(isArchived).length;
    return { active: containerTypes.length - archived, archived };
  }, [containerTypes]);

  const handleRowSelect = (type: ContainerType | null) => {
    setSelectedType(type);
  };

  const handleStatusChange = (status: BunkerTypeStatus) => {
    setStatusFilter(status);
    setSelectedType(null);
  };

  const setSelectedStatus = async (status: ConnectProto.ContainerTypeStatus) => {
    if (!selectedType) return;
    try {
      await updateContainerType({
        id: selectedType.id,
        ownerOrganizationId: selectedType.ownerOrganizationId,
        status,
      }).unwrap();
      toast({
        title: t(
          Namespace.Settings,
          status === ConnectProto.ContainerTypeStatus.INACTIVE
            ? "bunkerType.archived"
            : "bunkerType.restored",
        ),
      });
      setSelectedType(null);
    } catch (error) {
      toast({
        title: t(Namespace.Settings, "bunkerType.dialog.error"),
        description: describeError(error as ApiError),
        variant: "destructive",
      });
    }
  };

  const handleCreateClick = () => {
    setEditingType(null);
    setDialogOpen(true);
  };

  const handleEditClick = () => {
    if (selectedType) {
      setEditingType(selectedType);
      setDialogOpen(true);
    }
  };

  // Types of the whole holding are listed; one is edited or archived with a write right on its company.
  const canWrite = useCanWriteType("container_type", selectedCompanyId);
  const writableSelected = !!selectedType && canWrite(selectedType);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <BunkerTypeStatusFilter
        activeStatus={statusFilter}
        onStatusChange={handleStatusChange}
        counts={statusCounts}
        onCreateClick={handleCreateClick}
        // Another company's type opens read only
        onEditClick={selectedType ? handleEditClick : undefined}
        onArchiveClick={
          writableSelected && !isArchived(selectedType) && !isUpdating
            ? () => setSelectedStatus(ConnectProto.ContainerTypeStatus.INACTIVE)
            : undefined
        }
        onRestoreClick={
          writableSelected && isArchived(selectedType) && !isUpdating
            ? () => setSelectedStatus(ConnectProto.ContainerTypeStatus.ACTIVE)
            : undefined
        }
      />

      <BunkerTypesTable
        containerTypes={filteredTypes}
        isLoading={isLoading}
        selectedType={selectedType}
        onRowSelect={handleRowSelect}
      />

      <BunkerTypeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        containerType={editingType}
        ownerOrganizationId={selectedCompanyId ?? null}
      />
    </div>
  );
};
